import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { api, formatApiErrorDetail } from "../lib/api";
import { HELP_AREAS, IMAGES } from "../lib/content";

export default function GetInvolved() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    town: "",
    areas: [],
    website: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (a) =>
    setForm((f) => ({
      ...f,
      areas: f.areas.includes(a)
        ? f.areas.filter((x) => x !== a)
        : [...f.areas, a],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErr("Please enter your name and a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/membership", form);
      setDone(true);
      toast.success("Mahalo! Your membership sign-up was received.");
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <>
      <PageHero
        eyebrow="Community Membership"
        lines={[
          { text: "Join as a" },
          { text: "Community Member", className: "font-serifg italic font-semibold" },
        ]}
        lead="Free. Get updates, event news, donated-resource announcements, and first word on volunteer opportunities and Board-approved committees."
      />

      <section className="wrap py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <Reveal>
            {done ? (
              <div
                className="card-surface border-primary/40"
                data-testid="membership-success-card"
              >
                <CheckCircle2 className="h-10 w-10 text-primary dark:text-accent" />
                <h2 className="font-display text-2xl font-bold">
                  Mahalo, {form.name.split(" ")[0]}!
                </h2>
                <p className="text-muted-foreground">
                  Your Community Membership sign-up was received. Watch your
                  inbox for the next update, event news, and volunteer
                  opportunities.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="flex flex-col gap-4" data-testid="membership-form">
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="m-name">Full name</label>
                  <input id="m-name" className="field" type="text" autoComplete="name" required value={form.name} onChange={set("name")} data-testid="membership-name-input" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="label" htmlFor="m-email">Email</label>
                  <input id="m-email" className="field" type="email" autoComplete="email" required value={form.email} onChange={set("email")} data-testid="membership-email-input" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="m-phone">Phone (optional)</label>
                    <input id="m-phone" className="field" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="membership-phone-input" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="label" htmlFor="m-town">Town or island</label>
                    <input id="m-town" className="field" type="text" placeholder="e.g. Kapaʻa, Kauaʻi" value={form.town} onChange={set("town")} data-testid="membership-town-input" />
                  </div>
                </div>
                <fieldset className="rounded-xl border border-border p-4">
                  <legend className="px-1 text-sm font-bold">I can help with</legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {HELP_AREAS.map((a) => (
                      <label key={a} className="flex items-start gap-2 text-sm font-normal" data-testid="membership-help-option">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={form.areas.includes(a)}
                          onChange={() => toggleArea(a)}
                          data-testid={`membership-help-checkbox-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                        {a}
                      </label>
                    ))}
                  </div>
                </fieldset>
                {/* honeypot */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={set("website")}
                  className="pointer-events-none absolute left-[-9999px] h-0 w-0 opacity-0"
                />
                <p className="text-sm text-muted-foreground">
                  Community Membership does not provide governance, voting, or
                  funding authority, and does not create any right to
                  assistance.
                </p>
                {err && (
                  <p className="text-sm font-semibold text-destructive" role="alert" data-testid="membership-form-error">
                    {err}
                  </p>
                )}
                <button className="btn w-fit" type="submit" disabled={busy} data-testid="membership-form-submit">
                  {busy ? "Signing up…" : "Sign me up"}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col gap-5">
            <div className="overflow-hidden rounded-3xl">
              <img
                src={IMAGES.communityCouple}
                alt="Kauaʻi community members smiling together"
                className="h-64 w-full object-cover transition duration-700 hover:scale-105"
                data-testid="membership-image"
              />
            </div>
            <div className="callout">
              <b className="font-display">About volunteering</b>
              <p className="text-muted-foreground">
                Volunteers and Community Members can't bind the Foundation,
                make public promises on its behalf, accept funds, approve
                awards, or access confidential information unless separately
                authorized and trained.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
